import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Box, Button, Typography } from '@mui/material';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { OrganizationUserTable } from 'src/shared/components/organisms/organizationUserTable';
import { getAssociationUsers } from 'src/shared/services/associationService';
import { getOrganizationAssociationId } from 'src/shared/functions/organizationHelpers';
import { useGlobals } from 'src/shared/contexts/appContext';
import { PersonAdd } from '@mui/icons-material';
import { UserCreateModal } from 'src/modules/user/components/userCreateModal';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { AssociationUserResponse } from '../models/associationUserResponse';

export const AssociationUsersPage = () => {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState<boolean>(false);
  const [createMode, setCreateMode] = useState<boolean>(false);

  const { setDateStamp, currentUser } = useGlobals();
  const params = useParams();
  const currentAssocationId = getOrganizationAssociationId(currentUser, params.id);
  const [associationUsers, setAssociationUsers] = useState<AssociationUserResponse[]>([]);
  const isMounted = useIsMounted();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAssociationUsers(currentAssocationId);
      if (isMounted()) {
        setAssociationUsers(result.data);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [currentAssocationId, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = () => {
    setDateStamp(new Date());
    setCreateMode(false);
    fetchData();
  };

  return (
    <>
      <FormLoaderWrapper loading={loading}>
        <Box sx={{ p: 3, minHeight: '15rem', maxWidth: { md: '100%', lg: '100%', xl: '50%' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant='h6' gutterBottom>
              {t('common:organization.userPageHeader')}
            </Typography>
            {getPermission(currentUser, PermissionEnum.UserCreateAssociation) && (
              <Button onClick={() => setCreateMode(true)} variant='contained' color='primary' startIcon={<PersonAdd />} disableElevation>
                {t('user:createNewUserButton')}
              </Button>
            )}
          </Box>
          {associationUsers && (
            <OrganizationUserTable<AssociationUserResponse>
              roles={associationUsers}
              idColumn='localAssUser_id'
              titleColumn='name'
              valueColumn='userName'
              // editCallback={() => ConsoleHelper.log('x')} // Not used
              // deleteCallback={() => ConsoleHelper.log('x')} // Not used
            />
          )}
        </Box>
      </FormLoaderWrapper>
      {createMode && (
        <UserCreateModal
          onClose={() => setCreateMode(false)}
          onSave={() => handleSave()}
          modalOpen={createMode}
          preSelectedAssociation={currentAssocationId ? Number(currentAssocationId) : undefined}
        />
      )}
    </>
  );
};
